/**
 * Dashboard — Feature 3.
 * Aggregate view: stat cards, global category breakdown, recent 5 completed jobs.
 * Uses only existing CSS classes / inline styles (no external component library).
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { statusApi, downloadApi, cckCreditsApi } from '../services/api';
import { CATEGORY_COLORS, CATEGORY_LABELS, PhoneCategory, CckCreditSummary } from '../types';
import { RerunFailedAnalysisButton } from '../components/RerunFailedAnalysisButton';

interface DashboardData {
  total_jobs: number;
  completed_jobs: number;
  total_numbers: number;
  total_processed: number;
  category_totals: Record<string, number>;
  recent_jobs: RecentJob[];
}

interface RecentJob {
  job_id: string;
  filename: string;
  status: string;
  total_numbers: number;
  processed_count: number;
  category_counts: Record<string, number>;
  created_at: string;
  completed_at: string | null;
  export_ready: boolean;
}

// ── Stat card ─────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: string | number;
  icon: string;
  accent: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, accent }) => (
  <div style={{
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 2,
    padding: '20px 24px',
    boxShadow: '5px 5px 0 rgba(0,0,0,0.45)',
    flex: '1 1 180px',
    minWidth: 160,
  }}>
    <div style={{ fontSize: 24, marginBottom: 8 }}>{icon}</div>
    <div style={{ fontSize: 28, fontWeight: 700, color: accent, lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 6 }}>{label}</div>
  </div>
);

// ── Dial-credit usage widget (billing-credits-01) ──────────────────────────────
// Compact card matching the stat-card grid. Renders nothing unless the user is an
// enforced subscriber (exempt/admin/grandfathered/billing-off → hidden), so the
// existing layout is unchanged for non-billed accounts.

const UsageWidget: React.FC = () => {
  const [sm, setSm] = useState<CckCreditSummary | null>(null);

  useEffect(() => {
    let alive = true;
    cckCreditsApi.getSummary().then((s) => { if (alive) setSm(s); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!sm || !sm.metered) return null;   // unmetered (admin/exempt/both off) → hidden
  const trial = sm.cck.enabled ? sm.cck.balance : 0;
  const plan = sm.stripe?.spendable ? (sm.stripe.remaining ?? 0) : 0;

  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 2,
      padding: '20px 24px', boxShadow: '5px 5px 0 rgba(0,0,0,0.45)', flex: '1 1 180px', minWidth: 160,
    }}>
      <div style={{ fontSize: 24, marginBottom: 8 }}>💳</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--amber)', lineHeight: 1 }}>
        {sm.total_remaining.toLocaleString()}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-faint)', margin: '6px 0 8px' }}>
        credits available
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-soft)' }}>
        {trial.toLocaleString()} trial · {plan.toLocaleString()} plan
      </div>
    </div>
  );
};

// ── Category bar ──────────────────────────────────────────────────────────────

interface CategoryBarProps {
  totals: Record<string, number>;
}

const CategoryBar: React.FC<CategoryBarProps> = ({ totals }) => {
  const total = Object.values(totals).reduce((a, b) => a + b, 0);
  if (total === 0) return null;

  const sorted = Object.entries(totals).sort(([, a], [, b]) => b - a);

  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 2, padding: '20px 24px',
      boxShadow: '5px 5px 0 rgba(0,0,0,0.45)', marginBottom: 24,
    }}>
      <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-strong)', marginBottom: 16 }}>
        📊 Global Category Breakdown
      </div>

      {/* Stacked bar */}
      <div style={{
        display: 'flex', height: 20, borderRadius: 8,
        overflow: 'hidden', marginBottom: 16,
      }}>
        {sorted.map(([cat, count]) => (
          <div
            key={cat}
            title={`${CATEGORY_LABELS[cat as PhoneCategory] || cat}: ${count} (${((count / total) * 100).toFixed(1)}%)`}
            style={{
              width: `${(count / total) * 100}%`,
              background: CATEGORY_COLORS[cat as PhoneCategory] || 'var(--status-neutral-bg)',
              transition: 'width 0.3s',
            }}
          />
        ))}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {sorted.map(([cat, count]) => (
          <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
            <div style={{
              width: 10, height: 10, borderRadius: 2,
              background: CATEGORY_COLORS[cat as PhoneCategory] || 'var(--status-neutral-bg)',
              flexShrink: 0,
            }} />
            <span style={{ color: 'var(--text)' }}>
              {CATEGORY_LABELS[cat as PhoneCategory] || cat}:&nbsp;
              <strong>{count}</strong>
              <span style={{ color: 'var(--text-faint)', marginLeft: 3 }}>
                ({((count / total) * 100).toFixed(1)}%)
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const Dashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await statusApi.getDashboard();
        setData(res);
      } catch {
        setError('Failed to load dashboard data.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const fmt = (d: string) => new Date(d).toLocaleString();

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 60 }}>
      <div className="spinner" />
      <p className="text-muted mt-3">Loading dashboard…</p>
    </div>
  );

  if (error) return (
    <div className="alert alert-error" style={{ margin: '24px 0' }}>❌ {error}</div>
  );

  if (!data) return null;

  const successRate = data.total_numbers > 0
    ? ((data.total_processed / data.total_numbers) * 100).toFixed(1)
    : '0.0';

  const goodCount = data.category_totals['GOOD'] || 0;
  const goodRate = data.total_processed > 0
    ? ((goodCount / data.total_processed) * 100).toFixed(1)
    : '0.0';

  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text-strong)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>📈 Analytics</h2>
        <p style={{ margin: '4px 0 0', color: 'var(--text-faint)', fontSize: 14 }}>
          Aggregate overview across all Dial Sniper runs
        </p>
      </div>

      {/* ── Stat cards ── */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <StatCard label="Total Runs" value={data.total_jobs} icon="🗂️" accent="var(--amber)" />
        <StatCard label="Completed Runs" value={data.completed_jobs} icon="✅" accent="var(--green)" />
        <StatCard label="Numbers Dialed" value={data.total_numbers.toLocaleString()} icon="📞" accent="var(--status-info-fg)" />
        <StatCard label="Processed" value={data.total_processed.toLocaleString()} icon="⚡" accent="var(--teal-dark)" />
        <StatCard label="Process Rate" value={`${successRate}%`} icon="📊" accent="var(--amber)" />
        <StatCard label="Good (Human)" value={`${goodRate}%`} icon="🟢" accent="var(--green)" />
        <UsageWidget />
      </div>

      {/* ── Category breakdown bar ── */}
      {Object.keys(data.category_totals).length > 0 && (
        <CategoryBar totals={data.category_totals} />
      )}

      {/* ── Recent completed jobs ── */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 2, overflow: 'hidden',
        boxShadow: '5px 5px 0 rgba(0,0,0,0.45)',
      }}>
        <div style={{
          padding: '16px 24px', borderBottom: '1px solid var(--border-subtle)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-strong)' }}>
            🕐 Recent Completed Runs
          </div>
          <Link
            to="/history"
            style={{ fontSize: 13, color: 'var(--amber)', textDecoration: 'none', fontWeight: 600 }}
          >
            View All →
          </Link>
        </div>

        {data.recent_jobs.length === 0 ? (
          <div style={{ padding: '32px 24px', textAlign: 'center', color: 'var(--text-faint)', fontSize: 14 }}>
            No completed runs yet.{' '}
            <Link to="/upload" style={{ color: 'var(--amber)', textDecoration: 'none', fontWeight: 600 }}>
              Start a run →
            </Link>
          </div>
        ) : (
          <div>
            {data.recent_jobs.map((job, idx) => {
              const counts = job.category_counts ?? {};
              const topCats = Object.entries(counts)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 4);
              return (
                <div
                  key={job.job_id}
                  style={{
                    padding: '14px 24px',
                    borderBottom: idx < data.recent_jobs.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                    display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
                  }}
                >
                  {/* File + meta */}
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-strong)', marginBottom: 2 }}>
                      {job.filename}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-faint)' }}>
                      {job.processed_count.toLocaleString()} / {job.total_numbers.toLocaleString()} numbers
                      {job.completed_at && ` · ${fmt(job.completed_at)}`}
                    </div>
                  </div>

                  {/* Category chips */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {topCats.map(([cat, count]) => (
                      <span
                        key={cat}
                        style={{
                          padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                          background: CATEGORY_COLORS[cat as PhoneCategory] || 'var(--status-neutral-bg)',
                          color: 'var(--text)',
                        }}
                      >
                        {CATEGORY_LABELS[cat as PhoneCategory] || cat}: {count}
                      </span>
                    ))}
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    {job.export_ready && (
                      <button
                        onClick={() => downloadApi.triggerDownload(job.job_id, job.filename || `job-${job.job_id}.xlsx`)}
                        style={{
                          background: 'linear-gradient(135deg, #8a2000, #5c0000)', color: 'var(--text)', border: '2px solid var(--teal)',
                          padding: '6px 14px', borderRadius: 6,
                          cursor: 'pointer', fontSize: 12, fontWeight: 600,
                        }}
                      >
                        ⬇️ Download
                      </button>
                    )}
                    <Link
                      to={`/processing/${job.job_id}`}
                      style={{
                        background: 'var(--surface-subtle)', color: 'var(--text)',
                        padding: '6px 14px', borderRadius: 6,
                        textDecoration: 'none', fontSize: 12, fontWeight: 500,
                        border: '1px solid var(--border)',
                      }}
                    >
                      Details
                    </Link>
                    <RerunFailedAnalysisButton jobId={job.job_id} jobStatus={job.status} compact />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
